import React, { useState, useEffect } from 'react';
import { TypeProthese } from '../../types';

interface TypeProtheseFormProps {
  typeProthese?: TypeProthese | null;
  onSubmit: (data: Omit<TypeProthese, 'id' | 'created_at' | 'updated_at'>) => void;
  onCancel: () => void;
  loading: boolean;
}

const TypeProtheseForm: React.FC<TypeProtheseFormProps> = ({ 
  typeProthese, 
  onSubmit, 
  onCancel, 
  loading 
}) => {
  const [formData, setFormData] = useState<Omit<TypeProthese, 'id' | 'created_at' | 'updated_at'>>({
    nom: '',
    description: '',
    cout_base: 0,
    delai_fabrication: 7,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (typeProthese) {
      setFormData({
        nom: typeProthese.nom,
        description: typeProthese.description || '',
        cout_base: typeProthese.cout_base,
        delai_fabrication: typeProthese.delai_fabrication,
      });
    }
  }, [typeProthese]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? Number(value) : value
    }));
    
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.nom.trim()) {
      newErrors.nom = 'Le nom du type de prothèse est requis';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'La description est requise';
    }

    if (formData.cout_base <= 0) {
      newErrors.cout_base = 'Le coût de base doit être supérieur à 0';
    }

    if (formData.delai_fabrication < 1) {
      newErrors.delai_fabrication = 'Le délai doit être d\'au moins 1 jour';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      onSubmit(formData);
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'GNF',
      minimumFractionDigits: 0
    }).format(value);
  };

  return (
    <div className="max-w-2xl mx-auto bg-white rounded-lg shadow-md">
      <div className="px-6 py-4 border-b border-gray-200">
        <h2 className="text-xl font-semibold text-gray-800">
          {typeProthese ? 'Modifier' : 'Ajouter'} un type de prothèse
        </h2>
      </div>

      <form onSubmit={handleSubmit} className="p-6 space-y-6">
        {/* Nom */}
        <div>
          <label htmlFor="nom" className="block text-sm font-medium text-gray-700 mb-2">
            Nom du type de prothèse *
          </label>
          <input
            type="text"
            id="nom"
            name="nom"
            value={formData.nom}
            onChange={handleChange}
            className={`w-full px-3 py-2 border rounded-md text-gray-700 focus:outline-none focus:ring-2 focus:ring-teal-500 ${
              errors.nom ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder="Ex: Prothèse complète supérieure"
          />
          {errors.nom && <p className="mt-1 text-sm text-red-600">{errors.nom}</p>}
        </div>

        {/* Description */}
        <div>
          <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
            Description *
          </label>
          <textarea
            id="description"
            name="description"
            value={formData.description}
            onChange={handleChange}
            rows={4}
            className={`w-full px-3 py-2 border rounded-md text-gray-700 focus:outline-none focus:ring-2 focus:ring-teal-500 ${
              errors.description ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder="Description détaillée du type de prothèse..."
          />
          {errors.description && <p className="mt-1 text-sm text-red-600">{errors.description}</p>}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Coût de base */}
          <div>
            <label htmlFor="cout_base" className="block text-sm font-medium text-gray-700 mb-2">
              Coût de base (GNF) *
            </label>
            <input
              type="number"
              id="cout_base"
              name="cout_base"
              value={formData.cout_base}
              onChange={handleChange}
              min="0"
              step="1000"
              className={`w-full px-3 py-2 border rounded-md text-gray-700 focus:outline-none focus:ring-2 focus:ring-teal-500 ${
                errors.cout_base ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="500000"
            />
            {errors.cout_base && <p className="mt-1 text-sm text-red-600">{errors.cout_base}</p>}
            {formData.cout_base > 0 && (
              <p className="mt-1 text-sm text-gray-600">
                Aperçu: {formatCurrency(formData.cout_base)}
              </p>
            )}
          </div>

          {/* Délai de fabrication */}
          <div>
            <label htmlFor="delai_fabrication" className="block text-sm font-medium text-gray-700 mb-2">
              Délai de fabrication (jours) *
            </label>
            <input
              type="number"
              id="delai_fabrication"
              name="delai_fabrication"
              value={formData.delai_fabrication}
              onChange={handleChange}
              min="1"
              className={`w-full px-3 py-2 border rounded-md text-gray-700 focus:outline-none focus:ring-2 focus:ring-teal-500 ${
                errors.delai_fabrication ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {errors.delai_fabrication && <p className="mt-1 text-sm text-red-600">{errors.delai_fabrication}</p>}
          </div>
        </div>

        {/* Aperçu du type de prothèse */}
        {formData.nom && formData.cout_base > 0 && (
          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="font-medium text-gray-900 mb-2">Aperçu</h4>
            <div className="space-y-1 text-sm text-gray-600">
              <p><strong>Nom:</strong> {formData.nom}</p>
              <p><strong>Coût:</strong> {formatCurrency(formData.cout_base)}</p>
              <p><strong>Délai:</strong> {formData.delai_fabrication} jour{formData.delai_fabrication > 1 ? 's' : ''}</p>
              {formData.description && (
                <p><strong>Description:</strong> {formData.description.substring(0, 100)}{formData.description.length > 100 ? '...' : ''}</p>
              )}
            </div>
          </div>
        )}

        {/* Suggestions de prix */}
        <div className="bg-blue-50 p-4 rounded-lg">
          <h4 className="font-medium text-blue-900 mb-2">💡 Suggestions de prix (GNF)</h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
            <button
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, cout_base: 300000 }))}
              className="text-left p-2 bg-white rounded border hover:bg-gray-50"
            >
              <div className="font-medium">Prothèse partielle</div>
              <div className="text-gray-600">300,000 GNF</div>
            </button>
            <button
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, cout_base: 450000 }))}
              className="text-left p-2 bg-white rounded border hover:bg-gray-50"
            >
              <div className="font-medium">Prothèse complète</div>
              <div className="text-gray-600">450,000 GNF</div>
            </button>
            <button
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, cout_base: 600000 }))}
              className="text-left p-2 bg-white rounded border hover:bg-gray-50"
            >
              <div className="font-medium">Bridge</div>
              <div className="text-gray-600">600,000 GNF</div>
            </button>
            <button
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, cout_base: 800000 }))}
              className="text-left p-2 bg-white rounded border hover:bg-gray-50"
            >
              <div className="font-medium">Implant</div>
              <div className="text-gray-600">800,000 GNF</div>
            </button>
          </div>
        </div>

        {/* Boutons d'action */}
        <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500"
          >
            Annuler
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-4 py-2 text-sm font-medium text-white bg-teal-600 border border-transparent rounded-md hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Enregistrement...' : (typeProthese ? 'Modifier' : 'Ajouter')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default TypeProtheseForm;